import { useEffect } from "react";

const SITE_URL = "https://parlayassistant.com";

const setMeta = (name, value) => {
  if (!value) return null;
  let el = document.querySelector(`meta[name="${name}"]`);
  const prev = el ? el.getAttribute("content") : null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
  return { el, prev, name };
};

const setProperty = (property, value) => {
  if (!value) return null;
  let el = document.querySelector(`meta[property="${property}"]`);
  const prev = el ? el.getAttribute("content") : null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
  return { el, prev, property };
};

const setCanonical = (href) => {
  if (!href) return null;
  let el = document.querySelector('link[rel="canonical"]');
  const prev = el ? el.getAttribute("href") : null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return { el, prev };
};

/**
 * Update <title>, description, canonical, and OG/Twitter tags for the current page.
 * Optionally sets a robots meta directive (e.g. "noindex, follow" for pages we
 * don't want in search yet). All mutations are reverted on unmount so
 * client-side route changes stay clean.
 */
export const useSEO = ({ title, description, path = "/", robots }) => {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const patches = [];
    if (description) {
      patches.push(setMeta("description", description));
      patches.push(setProperty("og:description", description));
      patches.push(setMeta("twitter:description", description));
    }
    if (title) {
      patches.push(setProperty("og:title", title));
      patches.push(setMeta("twitter:title", title));
    }
    const url = `${SITE_URL}${path}`;
    patches.push(setProperty("og:url", url));
    const canonicalPatch = setCanonical(url);

    // robots meta: created on mount, removed on unmount so other pages
    // (which don't set robots) stay untouched.
    let robotsCreated = null;
    if (robots) {
      let el = document.querySelector('meta[name="robots"]');
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", "robots");
        document.head.appendChild(el);
        robotsCreated = el;
      }
      patches.push({ el, prev: el.getAttribute("content"), name: "robots" });
      el.setAttribute("content", robots);
    }

    return () => {
      document.title = prevTitle;
      patches.forEach((p) => {
        if (p && p.el && p.prev !== null) p.el.setAttribute("content", p.prev);
      });
      if (canonicalPatch && canonicalPatch.el && canonicalPatch.prev !== null) {
        canonicalPatch.el.setAttribute("href", canonicalPatch.prev);
      }
      if (robotsCreated && robotsCreated.parentNode) {
        robotsCreated.parentNode.removeChild(robotsCreated);
      }
    };
  }, [title, description, path, robots]);
};

export default useSEO;
